/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  ConstructionScheduleRestService,
  PrintFilterDto,
  ReportFilterDto,
  ReportRestService,
} from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { UserContext } from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import { DialogService, TextMessageDialogConfig, WaitingService } from '@gh/core-ui';
import { createLink } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { catchError$, switchMap$ } from '@gh/rx/operators';

import { get } from 'lodash';
import { Observable } from 'rxjs';

import { ReportEndpoint } from '../reports.constants';

export type ReportFilter = ReportFilterDto;
export type ReportDialogConfig = {
  title?: string;
  linkText?: string;
};

@Injectable()
export class ReportService {
  constructor(private reportRest: ReportRestService,
              private userContext: UserContext,
              private constructionScheduleRestService: ConstructionScheduleRestService,
              private translations: TranslationService,
              private waitingService: WaitingService,
              private dialogs: DialogService) {
  }

  /**
   * If user has permission to view specified report, this method will return URL to the this report,
   * otherwise 'about:blank'
   *
   * @param endpoint
   * @param filter
   */
  generateUrl(endpoint: ReportEndpoint, filter: ReportFilter): Observable<string> {
    return this.userContext.hasPermission(endpoint.permission)
      ? this.reportRest.generate(endpoint.name, filter).pipe(
        catchError$((errors) =>
          isMessageBasket(errors) && errors.has('msg_denied_no_permissions') ? ['about:blank'] : [errors]))
      : of$('');
  }

  generateUrlAndShowDialog(endpoint: ReportEndpoint,
                           filter: ReportFilter,
                           config: ReportDialogConfig = {}): Observable<void> {
    return this.reportRest.generate(endpoint.name, filter).pipe(
      switchMap$((url) =>
        this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
          title: this.translations.get(<string>get(config, 'title', 'lbl_link_to_report')),
          html: [
            this.translations.get(<string>get(config, 'linkText', 'lbl_link_text')),
            createLink(url),
          ].join(' '),
          buttons: [
            { value: 'close', title: this.translations.get('lbl_ok'), type: 'primary' },
          ],
        }).afterClosed()));
  }
  generatePrintUrlAndShowDialog(filter: PrintFilterDto): Observable<string> {
    return this.constructionScheduleRestService.print(filter);
  }
}
